import React, { useMemo } from "react";
import { Bar, Line } from "recharts";
import { Item, Order_Output as Order, OrderType, ProductionTask_Input } from "@/api";
import { formatDate, formatNumber } from "@/utils/format";
import { getChartColors, getImportantChartColors, renderCommonChart } from "./commonChart";

interface EchoOrderProductionChartProps {
  items: Item[];
  orders: Order[];
  dates: string[];
  productInventory: Record<Item["品目ID"], number[]>;
  productionTasks: ProductionTask_Input[];
}

const EchoOrderProductionChart: React.FC<EchoOrderProductionChartProps> = ({ items, orders, dates, productInventory, productionTasks }) => {
  const itemIds = useMemo(() => new Set(items.map((i) => i.品目ID)), [items]);
  const dateIndex = useMemo(() => new Map(dates.map((d, idx) => [d, idx])), [dates]);
  const dateSet = useMemo(() => new Set(dates), [dates]);
  const filteredOrders = useMemo(() => orders.filter((o) => dateSet.has(o.due_date)), [orders, dateSet]);

  const timeline = useMemo(() => {
    const set = new Set<string>();
    dates.forEach((d) => set.add(d));
    filteredOrders.forEach((o) => set.add(o.due_date));
    return Array.from(set).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  }, [dates, filteredOrders]);

  const chartData = useMemo(() => {
    return timeline.map((isoDate) => {
      const row: Record<string, string | number | unknown> = { date: formatDate(isoDate) };
      const destinations: Record<string, { confirmed: Record<string, number>; forecast: Record<string, number> }> = {};
      const prodDest: Record<string, number> = {};

      items.forEach((item) => {
        const baseId = item.品目ID;
        const matched = filteredOrders.filter((o) => o.sku_id === baseId && o.due_date === isoDate);
        const confirmed = matched
          .filter((o) => String(o.order_type) === "確定")
          .reduce((sum, o) => sum + (o.quantity ?? 0), 0);
        const forecast = matched
          .filter((o) => String(o.order_type) !== "確定")
          .reduce((sum, o) => sum + (o.quantity ?? 0), 0);

        const dest = (destinations[baseId] = destinations[baseId] ?? { confirmed: {}, forecast: {} });
        matched.forEach((o) => {
          const bucket = String(o.order_type) === "確定" ? dest.confirmed : dest.forecast;
          bucket[o.destination] = (bucket[o.destination] ?? 0) + (o.quantity ?? 0);
        });

        row[`${baseId}_confirmed`] = confirmed;
        row[`${baseId}_forecast`] = forecast;

        // 生産タスク集計（成形数）
        const prodQty = productionTasks
          .filter((task) => task.sku_id === baseId && task.date === isoDate)
          .reduce((sum, t) => sum + (t.quantity ?? 0), 0);
        row[`${baseId}_production`] = prodQty;

        const idx = dateIndex.get(isoDate);
        const invSeries = productInventory?.[baseId];
        const inventory = idx !== undefined && Array.isArray(invSeries) ? invSeries[idx] : undefined;
        if (inventory !== undefined) {
          row[`${baseId}_inventory`] = inventory;
        }
      });

      row.destinations = destinations;
      return row;
    });
  }, [items, filteredOrders, timeline]);

  const bars = useMemo(() => {
    return items.flatMap((item, idx) => {
      const baseId = item.品目ID;
      const confirmColor = getChartColors(idx);
      const forecastColor = getImportantChartColors(idx + 3);
      const productionColor = getImportantChartColors(idx + 6);
      return [
        <Bar
          key={`${baseId}-confirmed`}
          dataKey={`${baseId}_confirmed`}
          stackId={baseId}
          fill={confirmColor}
          name={`${item.品目ID} (確定)`}
          yAxisId="left"
        />,
        <Bar
          key={`${baseId}-forecast`}
          dataKey={`${baseId}_forecast`}
          stackId={baseId}
          fill={forecastColor}
          name={`${item.品目ID} (内示)`}
          opacity={0.7}
          yAxisId="left"
        />,
        <Bar
          key={`${baseId}-production`}
          dataKey={`${baseId}_production`}
          stackId={`${baseId}_production`}
          fill={productionColor}
          name={`${item.品目ID} (生産)`}
          opacity={0.85}
          yAxisId="left"
        />,
      ];
    });
  }, [items]);

  const inventoryLines = useMemo(() => {
    return items.map((item, idx) => (
      <Line
        key={`${item.品目ID}-inventory`}
        type="monotone"
        dataKey={`${item.品目ID}_inventory`}
        stroke={getImportantChartColors(idx + 5)}
        strokeWidth={2}
        dot={false}
        yAxisId="right"
        name={`${item.品目ID} 在庫`}
      />
    ));
  }, [items]);

  // すべてのバーがゼロの場合は簡易メッセージを返す
  const hasData = chartData.some((row) => {
    return Object.keys(row).some((k) => k !== "date" && (row as Record<string, number>)[k] > 0);
  });

  if (!hasData) {
    return <div className="text-sm text-gray-500">注文データがありません</div>;
  }

  const tooltipFormatter = (value: string | number, name: string, props: unknown): [string, string] => {
    const payload = (props as { payload?: any; dataKey?: string }).payload;
    const dataKey = (props as { dataKey?: string }).dataKey ?? "";

    if (/_confirmed$|_forecast$/i.test(dataKey)) {
      const baseId = dataKey.replace(/_(confirmed|forecast)$/i, "");
      const isConfirmed = dataKey.endsWith("_confirmed");
      const destMap = payload?.destinations?.[baseId]?.[isConfirmed ? "confirmed" : "forecast"] ?? {};
      const destText = Object.entries(destMap)
        .map(([dst, qty]) => `${dst}: ${formatNumber(Number(qty) || 0)}`)
        .join(", ") || "納入拠点情報なし";
      return [formatNumber(Number(value) || 0), `${name} / ${destText}`];
    }

    if (/_production$/i.test(dataKey)) {
      return [formatNumber(Number(value) || 0), `${name} / 生産数`];
    }

    if (dataKey.endsWith("_inventory")) {
      return [formatNumber(Number(value) || 0), name];
    }

    return [formatNumber(Number(value) || 0), name];
  };

  return renderCommonChart(
    chartData,
    [...bars, ...inventoryLines],
    false,
    tooltipFormatter,
    [0, 'auto'],
    true,
    { left: "数量", right: "在庫" },
    [0, 'auto'],
  );
};

export default EchoOrderProductionChart;
